import React, { useEffect, useRef, useState } from "react";
import {
  WIKI_VERSION,
  WIKI_VERSION_PRIOR,
} from "../config/wikiVersion";

const CLOSE_DELAY_MS = 280;

function ChangeList({ items, muted = false }) {
  return (
    <ul
      className={`space-y-1.5 text-xs leading-snug ${
        muted
          ? "text-slate-600 dark:text-slate-400"
          : "text-slate-700 dark:text-slate-300"
      }`}
    >
      {items.map((item) => (
        <li key={item} className="flex gap-1.5">
          <span
            className={`mt-1.5 h-1 w-1 shrink-0 rounded-full ${
              muted ? "bg-slate-300 dark:bg-slate-600" : "bg-slate-400 dark:bg-slate-500"
            }`}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function VersionBlock({ entry, latest = false }) {
  return (
    <section className={latest ? "" : "border-t border-slate-100 pt-3 dark:border-slate-700"}>
      <div className={latest ? "mb-2 border-b border-slate-100 pb-2 dark:border-slate-700" : "mb-1.5"}>
        <p
          className={`text-xs font-semibold ${
            latest
              ? "text-slate-900 dark:text-slate-100"
              : "text-slate-700 dark:text-slate-300"
          }`}
        >
          Version {entry.version}
        </p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">{entry.label}</p>
      </div>
      {latest && (
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Latest changes
        </p>
      )}
      <ChangeList items={entry.changes} muted={!latest} />
    </section>
  );
}

export default function WikiVersionBadge() {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef(null);
  const closeTimerRef = useRef(null);
  const { version } = WIKI_VERSION;

  const visible = open || pinned;

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (pinned) return;
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, CLOSE_DELAY_MS);
  };

  const closeAll = () => {
    cancelClose();
    setOpen(false);
    setPinned(false);
  };

  const handleOpen = () => {
    cancelClose();
    setOpen(true);
  };

  const handleBadgeClick = (event) => {
    event.stopPropagation();
    cancelClose();
    if (pinned) {
      closeAll();
      return;
    }
    setPinned(true);
    setOpen(true);
  };

  useEffect(() => {
    return () => cancelClose();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeAll();
      }
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeAll();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [visible]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className={`rounded px-1.5 py-0.5 text-[11px] font-medium leading-none transition-colors ${
          pinned
            ? "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        }`}
        aria-expanded={visible}
        aria-haspopup="dialog"
        aria-label={`Wiki version ${version}. ${visible ? "Hide" : "Show"} release notes.`}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onClick={handleBadgeClick}
      >
        v{version}
      </button>

      {visible && (
        <div
          className="absolute left-0 top-full z-50 w-72 pt-1 sm:w-80"
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
        >
          <div
            role="dialog"
            aria-label={`Release notes for version ${version}`}
            className="rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900"
          >
            <div
              className="max-h-72 space-y-3 overflow-y-auto overscroll-y-contain pr-1"
              onWheel={(event) => event.stopPropagation()}
            >
              <VersionBlock entry={WIKI_VERSION} latest />

              {WIKI_VERSION_PRIOR.length > 0 && (
                <section className="border-t border-slate-100 pt-3 dark:border-slate-700">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Version history
                  </p>
                  <div className="space-y-3">
                    {WIKI_VERSION_PRIOR.map((entry) => (
                      <VersionBlock key={entry.version} entry={entry} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            {!pinned && (
              <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] text-slate-400 dark:border-slate-700 dark:text-slate-500">
                Click v{version} to pin open and scroll history
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
